import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IconButton, InputAdornment, Paper, Popover, styled, useTheme, useMediaQuery, TextField } from '@mui/material';
import { EmojiEmotions, Send } from '@mui/icons-material';
import { useAddCommentMutation, useEditCommentMutation } from '../commentApiSlice';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentUserEmpCode } from '../../Auth/authSlice';
import EmojiPicker from 'emoji-picker-react';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: '6px 12px',
  display: 'flex',
  alignItems: 'flex-end',
  borderRadius: '20px',
  background: 'none',
  boxShadow: 'none',
  flexDirection: 'column',
  minHeight: '50px',
  position: 'absolute',
  bottom: '0rem',
  left: 0,
  right: 0,
  zIndex: 2,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: 20,
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
    '&.Mui-focused': {
      boxShadow: `${theme.palette.primary.main} 0 0 0 2px`,
    },
  },
}));

const validationSchema = Yup.object({
  feedback: Yup.string()
    .trim() // Trims whitespace from both ends of the string only
    .matches(/.*\S.*/, 'Feedback must contain at least one non-whitespace character.')
    .required('Feedback is required'),
});

const CommentForm = ({ editingComment, setEditingComment }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [addComment] = useAddCommentMutation();
  const [editComment] = useEditCommentMutation();
  const { empCode } = useParams();
  const userEmpCode = useSelector(selectCurrentUserEmpCode);
  const inputRef = useRef(null); // Ref for the input to maintain focus

  const formik = useFormik({
    initialValues: {
      feedback: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      inputRef.current.focus();
      const trimmedFeedback = values.feedback.trim();
      if (trimmedFeedback) {
        const action = editingComment
          ? editComment({ id: editingComment.commentId, data: { commentText: trimmedFeedback } })
          : addComment({ commentText: trimmedFeedback, empCode: empCode, authorEmpCode: userEmpCode });

        try {
          // await toast.promise(action.unwrap(), {
          //   loading: 'Processing comment...',
          //   success: editingComment ? 'Comment edited successfully!' : 'Comment submitted successfully!',
          //   error: (err) => err.data?.message || 'Error processing comment. Please try again.',
          // });
          await action.unwrap();
          setEditingComment(null);
          resetForm();
          setTimeout(() => inputRef.current.focus(), 0);
        } catch (error) {
          console.error('Error processing comment:', error);
        }
      }
    },
  });

  useEffect(() => {
    if (editingComment) {
      formik.setValues({ feedback: editingComment.commentText });
      if (inputRef.current) inputRef.current.focus();
    }
  }, [editingComment]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleEmojiPopover = (event) => {
    setAnchorEl(event.currentTarget);
    inputRef.current.focus();
  };

  const handleEmojiClick = (emojiObject) => {
    const newText = formik.values.feedback + emojiObject.emoji;
    formik.setFieldValue('feedback', newText);
    setAnchorEl(null); // Close the popover after selecting an emoji
    inputRef.current.focus();
  };

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        const trimmedFeedback = formik.values.feedback.trim();
        if (!event.shiftKey) {
          event.preventDefault();
          if (trimmedFeedback) {
            // Only submit if there's meaningful content
            formik.handleSubmit();
          }
        } else {
          // Allow line breaks in formik managed state
          formik.setFieldValue('feedback', formik.values.feedback);
        }
      }
    },
    [formik]
  );

  return (
    <StyledPaper elevation={3}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          inputRef.current.focus(); // Refocus on input to keep the keyboard open
          formik.handleSubmit();
        }}
        style={{ width: '100%', display: 'flex', alignItems: 'center', position: 'relative' }}
      >
        <StyledTextField
          multiline
          fullWidth
          inputRef={inputRef}
          id="feedback"
          name="feedback"
          placeholder="Type a message..."
          value={formik.values.feedback}
          onChange={formik.handleChange}
          // onBlur={(e) => e.preventDefault()}
          variant="outlined"
          // onKeyPress={handleKeyPress}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleEmojiPopover} edge="start">
                  <EmojiEmotions />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton type="submit" edge="end" onClick={() => inputRef.current.focus()}>
                  <Send />
                </IconButton>
              </InputAdornment>
            ),
          }}
          minRows={1}
          maxRows={isMobile ? 5 : 4}
        />
        <Popover
          id="emoji-picker-popover"
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <EmojiPicker onEmojiClick={handleEmojiClick} />
        </Popover>
      </form>
    </StyledPaper>
  );
};

export default CommentForm;
